import React, {useCallback, useEffect, useRef} from 'react';
import {GoogleMap, Libraries, LoadScript} from '@react-google-maps/api';

import './MapView.scss';
import {LatLng, Poi, Trip} from '../../../react-app-env';
import {calculateInitialBounds} from './InitialBoundsCalculator';
import {Events, EventType} from '../../../events/Events';
import {Breakpoint, getBreakpoint} from '../../../utils/Breakpoint';

export interface MapProps {
  googleMapsApiKey: string;
  onGoogleMapsApiLoaded?: (api: google.maps.Map) => void;
  trip: Trip;
}

const GOOGLE_MAPS_LIBS: Libraries = ['places'];

const MapView: React.FC<MapProps> = ({ trip, googleMapsApiKey, onGoogleMapsApiLoaded }) => {
  const mapRef: React.RefObject<google.maps.Map | null> = useRef(null);
  const divRef: React.RefObject<HTMLElement | null> = useRef(null);

  const options = {
    // styles:           styles,
    disableDefaultUI: true,
    zoomControl:      false
  };

  const handleMapLoad = useCallback((mapInstance: google.maps.Map) => {
    mapRef.current = mapInstance;
    divRef.current = mapInstance.getDiv();
    divRef.current.classList.add('mapview');

    Events.publish(EventType.SHOW_TRIP_IN_MAP);

    if (onGoogleMapsApiLoaded) {
      onGoogleMapsApiLoaded(mapInstance);
    }
  }, [onGoogleMapsApiLoaded]);

  // Watch for SHOW_TRIP_IN_MAP events.
  useEffect(() => {
    const unsubscribe = Events.subscribe(EventType.SHOW_TRIP_IN_MAP, () => {
      const coords = trip.pois.map((poi: Poi) => poi.location);
      const fittedBounds = calculateInitialBounds(coords);
      mapRef.current?.panTo(fittedBounds.center);
      mapRef.current?.setZoom(fittedBounds.zoom);
    });
    return () => unsubscribe.unsubscribe();
  }, [trip]);

  // Watch for CENTER_IN_MAP events.
  useEffect(() => {
    Events.subscribe(EventType.CENTER_IN_MAP, (location: LatLng) => {
      // If we're showing the Sidebar, offset the longitude a bit to account for that
      let lngOffset = 0;
      if (getBreakpoint() !== Breakpoint.PHONE) {
        lngOffset = 0.001;
      }

      mapRef.current?.panTo({
        lat: location.lat,
        lng: location.lng - lngOffset
      });
      mapRef.current?.setZoom(18);
    });
  }, []);

  return (
      <LoadScript googleMapsApiKey={googleMapsApiKey}
                  libraries={GOOGLE_MAPS_LIBS}>
        <GoogleMap options={options}
                   onLoad={handleMapLoad}></GoogleMap>
      </LoadScript>
  );

};

export default MapView;
